import { mergeAttributes, Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { HtmlCodeBlockView } from "./HtmlCodeBlockView";

export interface HtmlCodeBlockOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    htmlCodeBlock: {
      /** Insert an HTML code block with raw HTML content */
      insertHtmlCodeBlock: (attrs: {
        code: string;
        id?: string;
      }) => ReturnType;
    };
  }
}

export const HtmlCodeBlock = Node.create<HtmlCodeBlockOptions>({
  name: "htmlCodeBlock",

  group: "block",

  atom: true,

  selectable: true,

  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      code: {
        default: "",
        parseHTML: (element) =>
          element.getAttribute("data-code") || "",
        renderHTML: (attributes) => ({
          "data-code": attributes.code as string,
        }),
      },
      id: {
        default: "",
        parseHTML: (element) =>
          element.getAttribute("data-id") || "",
        renderHTML: (attributes) => ({
          "data-id": attributes.id as string,
        }),
      },
    };
  },

  parseHTML() {
    return [
      // New format
      { tag: `div[data-type="html-code-block"]` },
      // Legacy Quill format
      { tag: "div.html-code-blot" },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-type": "html-code-block",
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(HtmlCodeBlockView);
  },

  addCommands() {
    return {
      insertHtmlCodeBlock:
        (attrs) =>
        ({ commands }) => {
          const id = attrs.id || `html-${Date.now()}`;
          return commands.insertContent({
            type: this.name,
            attrs: {
              code: attrs.code,
              id,
            },
          });
        },
    };
  },
});
